import type { ReactNode } from "react";

import { cx } from "@/lib/cx";

type Tone = "neutral" | "accent" | "success" | "warning" | "danger" | "info";

const tones: Record<Tone, { bg: string; dot: string }> = {
  neutral: { bg: "bg-surface-sunken text-fg-muted", dot: "bg-fg-subtle" },
  accent: { bg: "bg-accent-subtle text-accent-text", dot: "bg-accent-indicator" },
  success: { bg: "bg-success-subtle text-success-text", dot: "bg-success" },
  warning: { bg: "bg-warning-subtle text-warning-text", dot: "bg-warning" },
  danger: { bg: "bg-danger-subtle text-danger-text", dot: "bg-danger" },
  info: { bg: "bg-info-subtle text-info-text", dot: "bg-info" },
};

/** Status is always conveyed by text; the color dot is supplementary. */
export function Badge({ tone = "neutral", children, dot = true, className }: { tone?: Tone; children: ReactNode; dot?: boolean; className?: string }) {
  return (
    <span className={cx("inline-flex h-5 items-center gap-1.5 rounded-sm px-1.5 text-xs font-medium whitespace-nowrap", tones[tone].bg, className)}>
      {dot && <span aria-hidden className={cx("size-1.5 rounded-full", tones[tone].dot)} />}
      {children}
    </span>
  );
}

const statusTone: Record<string, Tone> = {
  active: "success",
  pending: "info",
  accepted: "success",
  suspended: "warning",
  inactive: "neutral",
  archived: "neutral",
  revoked: "neutral",
  expired: "warning",
};

export function StatusBadge({ status }: { status: string }) {
  const label = status.charAt(0).toUpperCase() + status.slice(1);
  return <Badge tone={statusTone[status] ?? "neutral"}>{label}</Badge>;
}
